// SubscriptionTab.jsx — 테넌트 관리 '구독' 탭 (SaasTenantManagement.jsx 에서 옮김, 2026-10-25 요금제 4-2)
//   상태(체험·사용·정지·해지) · 요금제 고르기 · 인원 한도 · 기간 종료 · 메모.
//   요금제를 바꿔 저장하면 서버가 테넌트 메뉴를 그 요금제대로 맞춘다(확인 창에 켜지고 꺼질 메뉴를 먼저 보여 줌).
//   손으로 메뉴를 바꿨거나 요금제 내용을 고쳤으면(결정 1-b) '요금제와 다름' + [요금제대로 맞추기].
import React, { useEffect, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import { input, btn, btnOutline, card, TenantPicker } from './saasShared';
import PaymentsPanel from './PaymentsPanel';

// 정지·해지 = 그 테넌트 계정 전부 로그인 막힘(서버 tenant-login-gate)
const SUB_STATUSES = [
  { value: 'trial', label: '체험' },
  { value: 'active', label: '사용' },
  { value: 'suspended', label: '정지' },
  { value: 'canceled', label: '해지' },
];
const STOPPED_SUB = new Set(['suspended', 'canceled']);
const won = (n) => `${Number(n).toLocaleString('ko-KR')}원`;
const warnBox = { border: '1px solid #f59e0b', background: 'rgba(245,158,11,0.08)', borderRadius: 8, padding: '10px 12px', fontSize: 12, lineHeight: 1.7, display: 'flex', flexDirection: 'column', gap: 6 };

// 화면 미리보기 — 서버도 같은 계산(요금제 메뉴 ↔ 켜진 메뉴 차이)으로 맞춘다
function previewDiff(enabled, plan) {
  if (!plan) return { on: [], off: [] };
  const have = new Set(enabled.map((m) => m.code));
  const want = new Set(plan.modules || []);
  return { on: (plan.modules || []).filter((c) => !have.has(c)), off: enabled.filter((m) => !want.has(m.code)).map((m) => m.code) };
}

export default function SubscriptionTab({ tenants, selected, setSelected, showToast, onChanged, planInfo }) {
  const cur = tenants.find((t) => t.code === selected);
  const info = planInfo?.tenants?.[selected] || null;
  const plans = planInfo?.plans || [];
  const [status, setStatus] = useState('active');
  const [planCode, setPlanCode] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [staffLimit, setStaffLimit] = useState('');
  const [note, setNote] = useState('');
  useEffect(() => {
    if (!cur) return;
    setStatus(cur.subscription_status || 'active');
    setPlanCode(cur.plan_code || '');
    setPeriodEnd(cur.current_period_end || '');
    setStaffLimit(info?.staff_limit_override ? String(info.staff_limit_override) : '');
    setNote(info?.note || '');
  }, [selected, cur, info]);

  const isPlatform = cur?.tenant_type === 'platform';
  const savedStopped = STOPPED_SUB.has(cur?.subscription_status);
  const plan = plans.find((p) => p.code === planCode) || null;
  const nameOf = (code) => (planInfo?.modules || []).find((m) => m.code === code)?.name
    || (info?.enabled || []).find((m) => m.code === code)?.name || code;
  const listText = (codes) => (codes.length ? `: ${codes.map(nameOf).join(', ')}` : '');

  async function save() {
    if (STOPPED_SUB.has(status) && !savedStopped) {
      const label = SUB_STATUSES.find((s) => s.value === status)?.label;
      const ok = window.confirm(`'${cur?.name || selected}' 구독을 '${label}'(으)로 바꿀까요?\n\n이 테넌트의 모든 계정(테넌트 관리자 포함)이 15분 안에 로그아웃되고, 다시 로그인할 수 없어요.\n기록은 그대로 남고, '사용'이나 '체험'으로 되돌리면 바로 다시 로그인할 수 있어요.`);
      if (!ok) return;
    }
    if (plan && planCode !== (cur?.plan_code || '')) {
      const d = previewDiff(info?.enabled || [], plan);
      const ok = window.confirm([
        `'${cur?.name || selected}' 에 '${plan.name}' 요금제를 붙여요. 메뉴를 요금제대로 맞춥니다.`,
        '',
        `켜지는 메뉴 ${d.on.length}개${listText(d.on)}`,
        `꺼지는 메뉴 ${d.off.length}개${listText(d.off)}`,
        '',
        '꺼지는 메뉴는 그 테넌트 직원 모두에게서 바로 사라져요(기록은 남아요).',
      ].join('\n'));
      if (!ok) return;
    }
    const res = await apiClient.put(`/api/admin/saas/tenants/${selected}/subscription`, {
      status,
      planCode: planCode || null,
      currentPeriodEnd: periodEnd || null,
      note,
      staffLimit: isPlatform ? undefined : staffLimit,
    });
    if (!res.ok) { showToast(res.data?.message || '저장 실패', 'error'); return; }
    const ap = res.data?.data?.applied;
    if (ap?.failed?.length) showToast(`저장했지만 메뉴 ${ap.failed.length}개는 못 바꿨어요: ${ap.failed.join(', ')}`, 'error');
    else if (ap) showToast(`저장했어요. 메뉴 ${ap.enabled.length}개를 켜고 ${ap.disabled.length}개를 껐어요.`, 'success');
    else showToast('구독을 저장했어요.', 'success');
    await onChanged();
  }

  async function applyPlan() {
    const ok = window.confirm([
      `'${cur?.name || selected}' 메뉴를 '${info?.plan_name}' 요금제대로 맞출까요?`,
      '',
      `켜지는 메뉴 ${info?.to_enable?.length || 0}개${listText((info?.to_enable || []).map((m) => m.code))}`,
      `꺼지는 메뉴 ${info?.to_disable?.length || 0}개${listText((info?.to_disable || []).map((m) => m.code))}`,
    ].join('\n'));
    if (!ok) return;
    const res = await apiClient.post(`/api/admin/saas/tenants/${selected}/apply-plan`, {});
    if (!res.ok) { showToast(res.data?.message || '맞추기 실패', 'error'); return; }
    const ap = res.data?.data || {};
    if (ap.failed?.length) showToast(`메뉴 ${ap.failed.length}개는 못 바꿨어요: ${ap.failed.join(', ')}`, 'error');
    else showToast(`요금제대로 맞췄어요. 메뉴 ${ap.enabled?.length || 0}개를 켜고 ${ap.disabled?.length || 0}개를 껐어요.`, 'success');
    await onChanged();
  }

  const showDiffer = info?.differs && planCode === (cur?.plan_code || '');
  return (
    <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 560 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 13 }}>테넌트</strong>
        <TenantPicker tenants={tenants} selected={selected} setSelected={setSelected} />
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.6 }}>
        체험·사용 = 로그인 가능 / 정지·해지 = 이 테넌트의 모든 계정 로그인 막힘(기록은 그대로).
      </div>
      {savedStopped && !isPlatform && (
        <div style={{ fontSize: 12, fontWeight: 700, color: '#dc2626' }}>지금 이 테넌트는 로그인이 막혀 있어요.</div>
      )}
      {info?.expired_days > 0 && (
        <div style={{ fontSize: 12, fontWeight: 700, color: '#dc2626' }}>기간이 {info.period_end}에 끝났어요({info.expired_days}일 지남). 자동으로 막지는 않아요.</div>
      )}
      <label style={{ fontSize: 12 }}>상태
        <select style={{ ...input, marginLeft: 8 }} value={status} onChange={(e) => setStatus(e.target.value)}>
          {SUB_STATUSES.map((s) => (
            <option key={s.value} value={s.value} disabled={isPlatform && STOPPED_SUB.has(s.value)}>{s.label}</option>
          ))}
        </select>
      </label>
      {isPlatform && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>운영사 테넌트는 정지·해지할 수 없고, 요금제·인원 한도를 두지 않아요.</div>}
      <label style={{ fontSize: 12 }}>요금제
        <select style={{ ...input, marginLeft: 8, maxWidth: '100%' }} value={planCode} disabled={isPlatform} onChange={(e) => setPlanCode(e.target.value)}>
          <option value="">요금제 없음</option>
          {plans.map((p) => (
            <option key={p.code} value={p.code}>{p.name}{p.monthly_price != null ? ` · 월 ${won(p.monthly_price)}` : ''} · 메뉴 {(p.modules || []).length}개</option>
          ))}
          {info?.plan_missing && <option value={cur?.plan_code}>(없는 요금제 {cur?.plan_code})</option>}
        </select>
      </label>
      {plan && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.6 }}>
          메뉴: {(plan.module_names || []).join(', ') || '-'} · 기본 인원 {plan.staff_limit ? `${plan.staff_limit}명` : '제한 없음'}
        </div>
      )}
      {showDiffer && (
        <div style={warnBox}>
          <strong style={{ color: '#b45309' }}>요금제와 다름</strong>
          {info.to_enable.length > 0 && <div>요금제엔 있는데 꺼진 메뉴: {info.to_enable.map((m) => m.name).join(', ')}</div>}
          {info.to_disable.length > 0 && <div>요금제엔 없는데 켜진 메뉴: {info.to_disable.map((m) => m.name).join(', ')}</div>}
          <div><button style={btnOutline} onClick={applyPlan}>요금제대로 맞추기</button></div>
        </div>
      )}
      {!isPlatform && (
        <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>인원 한도
          <input
            style={{ ...input, width: 110 }}
            type="text"
            inputMode="numeric"
            placeholder={plan?.staff_limit ? `${plan.staff_limit}(요금제)` : '제한 없음'}
            value={staffLimit}
            onChange={(e) => setStaffLimit(e.target.value.replace(/[^0-9]/g, ''))}
          />
          명
          {info && <span style={{ fontSize: 12, fontWeight: 700 }}>(지금 {info.active_count ?? 0}명)</span>}
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>비우면 요금제 기본값을 써요. 한도를 넘으면 계정 추가·다시 재직이 막혀요.</span>
        </label>
      )}
      <label style={{ fontSize: 12 }}>현재 기간 종료 <input type="date" style={{ ...input, marginLeft: 8 }} value={periodEnd || ''} onChange={(e) => setPeriodEnd(e.target.value)} /></label>
      <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 8 }}>메모
        <input style={{ ...input, flex: 1, minWidth: 0 }} placeholder="(선택)" value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      <div><button style={btn} onClick={save}>저장</button></div>
      {!isPlatform && cur && <PaymentsPanel tenantCode={selected} tenantName={cur.name} showToast={showToast} onChanged={onChanged} />}
    </div>
  );
}
